'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowUp,
  ExternalLink,
  Share2,
  Eye,
  MessageCircle,
  MessageSquare,
  Calendar,
  X,
  Star,
  ThumbsUp,
  ThumbsDown,
  ChevronDown,
  Reply,
  Send,
  Trophy,
  Tag,
  Users,
  Package,
  ChevronRight,
  MoreHorizontal,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';

import Image from 'next/image';
import { usePageBuilderContext } from '../page-builder-context';
import { BlockPreview } from './block-preview';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth/auth-context';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

// ============================================================================
// STATUS TAG CONFIG
// ============================================================================

const STATUS_TAG_INFO: Record<string, { label: string; icon: string; color: string; bg: string }> = {
  investment: { label: 'Ищет инвестиции', icon: '💰', color: 'text-amber-600', bg: 'bg-amber-500/10' },
  feedback: { label: 'Нужен фидбек', icon: '🔥', color: 'text-rose-600', bg: 'bg-rose-500/10' },
  partnerships: { label: 'Ищет партнёров', icon: '🤝', color: 'text-blue-600', bg: 'bg-blue-500/10' },
  mentors: { label: 'Ищет менторов', icon: '🎓', color: 'text-violet-600', bg: 'bg-violet-500/10' },
  hiring: { label: 'Нанимает', icon: '👨‍💻', color: 'text-emerald-600', bg: 'bg-emerald-500/10' },
  // Legacy keys for backwards compatibility
  seeking_investment: { label: 'Ищет инвестиции', icon: '💰', color: 'text-amber-600', bg: 'bg-amber-500/10' },
  want_feedback: { label: 'Нужен фидбек', icon: '🔥', color: 'text-rose-600', bg: 'bg-rose-500/10' },
  open_to_partnerships: { label: 'Ищет партнёров', icon: '🤝', color: 'text-blue-600', bg: 'bg-blue-500/10' },
  looking_for_mentors: { label: 'Ищет менторов', icon: '🎓', color: 'text-violet-600', bg: 'bg-violet-500/10' },
};

// ============================================================================
// FEATURED PREVIEW SECTION - Bento layout for preview images
// ============================================================================

function FeaturedPreviewSection() {
  const { formData } = usePageBuilderContext();
  const { header, metadata } = formData;
  const previewImages = metadata.previewImages || [];

  // If no preview images, don't render this section
  if (previewImages.length === 0) {
    return null;
  }

  const mainImage = previewImages[0];
  const thumbnails = previewImages.slice(1, 5);

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Превью в «Избранном»</h2>
            <p className="text-sm text-foreground-secondary mt-0.5">
              Так ваш продукт будет выглядеть на главной странице
            </p>
          </div>
          <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">
            <Star className="h-3 w-3 mr-1" />
            Featured
          </Badge>
        </div>
      </div>

      <div className="p-6">
        {/* Bento Grid Layout */}
        <div className="relative rounded-xl overflow-hidden bg-black">
          {/* Main Image */}
          <div className="relative aspect-video">
            <img
              src={mainImage.imageUrl}
              alt={header.name || 'Preview'}
              className="w-full h-full object-cover"
            />

            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

            {/* Bottom Content Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
              <div className="flex items-end justify-between gap-4">
                <div className="flex-1 min-w-0">
                  {/* Rank Badge */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🥇</span>
                    <h3 className="text-xl md:text-2xl font-bold text-white truncate">
                      {header.name || 'Название продукта'}
                    </h3>
                  </div>

                  {/* Tagline */}
                  <p className="text-sm text-white/90 mb-3 line-clamp-2">
                    {header.tagline || 'Краткое описание продукта'}
                  </p>

                  {/* Status Tags */}
                  {metadata.statusTags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {metadata.statusTags.slice(0, 3).map((tag) => {
                        const info = STATUS_TAG_INFO[tag];
                        if (!info) return null;
                        return (
                          <Badge
                            key={tag}
                            variant="secondary"
                            className="text-xs bg-white/20 text-white border-white/30"
                          >
                            {info.icon} {info.label}
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* View Button */}
                <Button size="sm" className="flex-shrink-0">
                  Смотреть
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Thumbnail Strip - Only show if there are additional images */}
          {thumbnails.length > 0 && (
            <div className="bg-black/80 p-2">
              <div className="flex gap-2">
                {/* Main thumbnail (selected) */}
                <div className="relative w-20 h-12 rounded overflow-hidden ring-2 ring-primary cursor-pointer flex-shrink-0">
                  <img
                    src={mainImage.imageUrl}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Other thumbnails */}
                {thumbnails.map((img, index) => (
                  <div
                    key={index}
                    className="relative w-20 h-12 rounded overflow-hidden opacity-60 hover:opacity-100 transition-opacity cursor-pointer flex-shrink-0"
                  >
                    <img
                      src={img.imageUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}

                {/* Placeholder slots if less than 4 thumbnails */}
                {Array(Math.max(0, 3 - thumbnails.length)).fill(null).map((_, index) => (
                  <div
                    key={`placeholder-${index}`}
                    className="w-20 h-12 rounded border border-dashed border-white/20 flex items-center justify-center flex-shrink-0"
                  >
                    <span className="text-xs text-white/30">+</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Info Text */}
        <p className="text-xs text-foreground-tertiary text-center mt-4">
          Добавьте до 5 изображений в формате 16:9 для лучшего отображения
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// MOCK DATA
// ============================================================================

const MOCK_FOUNDER = {
  name: 'Алексей Петров',
  username: 'alexey',
  avatarUrl: '/mock-avatars/alexey.svg',
  productsLaunched: 3,
  followers: 1247,
};

const MOCK_METRICS = {
  voteCount: 127,
  viewCount: 2483,
  commentCount: 18,
  weekRank: 1,
  launchDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
};

interface PreviewComment {
  id: string;
  userName: string;
  userAvatar: string;
  content: string;
  voteCount: number;
  hasUserVoted?: boolean;
  createdAt: Date;
  replies: PreviewComment[];
}

const MOCK_COMMENTS: PreviewComment[] = [
  {
    id: '1',
    userName: 'Мария Иванова',
    userAvatar: '/mock-avatars/alexey.svg',
    content: 'Отличный продукт! Очень удобный интерфейс и продуманный UX. Особенно понравилась интеграция с Telegram.',
    voteCount: 24,
    hasUserVoted: true,
    createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000),
    replies: [
      {
        id: '1-1',
        userName: 'Алексей Петров',
        userAvatar: '/mock-avatars/alexey.svg',
        content: 'Спасибо за отзыв! Мы долго работали над UX, рады что это заметно.',
        voteCount: 8,
        createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
        replies: [],
      },
    ],
  },
  {
    id: '2',
    userName: 'Дмитрий Козлов',
    userAvatar: '/mock-avatars/dmitry.svg',
    content: 'Интересная идея. Подскажите, планируете ли добавить поддержку API для интеграции?',
    voteCount: 12,
    createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000),
    replies: [],
  },
];

const MOCK_QA_ITEMS = [
  {
    id: 'qa1',
    question: 'Как вы планируете монетизировать продукт?',
    answer: 'У нас будет freemium модель: базовый функционал бесплатно, продвинутые возможности по подписке от 990₽/мес.',
    askedBy: 'Игорь Сидоров',
    upvotes: 15,
    answeredAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
  },
  {
    id: 'qa2',
    question: 'Чем вы отличаетесь от конкурентов?',
    answer: 'Мы фокусируемся на российском рынке: поддержка местных платежей, интеграция с сервисами и локализация под местные требования.',
    askedBy: 'Анна Смирнова',
    upvotes: 23,
    answeredAt: new Date(Date.now() - 12 * 60 * 60 * 1000),
  },
];

const MOCK_RELATED_PRODUCTS = [
  {
    id: 'rel1',
    name: 'CloudTask',
    tagline: 'Умное управление задачами',
    logoUrl: '/mock-logos/cloudtask.svg',
    voteCount: 89,
  },
  {
    id: 'rel2',
    name: 'MetricFlow',
    tagline: 'Аналитика в реальном времени',
    logoUrl: '/mock-logos/metricflow.svg',
    voteCount: 156,
  },
  {
    id: 'rel3',
    name: 'TeamSync',
    tagline: 'Синхронизация для команд',
    logoUrl: '/mock-logos/teamsync.svg',
    voteCount: 72,
  },
  {
    id: 'rel4',
    name: 'DataPipe',
    tagline: 'ETL для стартапов',
    logoUrl: '/mock-logos/datapipe.svg',
    voteCount: 94,
  },
];

// ============================================================================
// HERO SECTION - Full width, all key info
// ============================================================================

function HeroSection() {
  const { formData, categories } = usePageBuilderContext();
  const { header, metadata } = formData;
  const { user } = useAuth();
  const [voted, setVoted] = useState(false);

  const selectedCategories = categories.filter((cat) => metadata.categoryIds.includes(cat.id));
  const voteCount = voted ? MOCK_METRICS.voteCount + 1 : MOCK_METRICS.voteCount;

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      {/* Main Hero Content */}
      <div className="p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-start gap-6">
          {/* Logo */}
          <div className="w-20 h-20 md:w-28 md:h-28 flex-shrink-0">
            {header.logo ? (
              <Image src={header.logo.originalUrl} alt="" width={112} height={112} className="w-full h-full object-cover rounded-xl" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-muted border border-border rounded-xl">
                <span className="text-3xl md:text-4xl font-bold text-muted-foreground">
                  {header.name?.charAt(0)?.toUpperCase() || 'P'}
                </span>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            {/* Title Row */}
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                {header.name || 'Название продукта'}
              </h1>
              <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">
                <Trophy className="h-3 w-3 mr-1" />
                #{MOCK_METRICS.weekRank} недели
              </Badge>
            </div>

            {/* Tagline */}
            <p className="text-lg text-foreground-secondary mb-4">
              {header.tagline || 'Краткое описание вашего продукта'}
            </p>

            {/* Status Tags */}
            {metadata.statusTags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {metadata.statusTags.map((tag) => {
                  const info = STATUS_TAG_INFO[tag];
                  if (!info) return null;
                  return (
                    <span key={tag} className={cn("inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium", info.bg, info.color)}>
                      <span>{info.icon}</span>
                      {info.label}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Categories */}
            {selectedCategories.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <Tag className="h-4 w-4 text-foreground-tertiary" />
                {selectedCategories.map((cat) => (
                  <Badge key={cat.id} variant="secondary" className="text-xs">
                    {cat.name}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Vote Button - Desktop */}
          <button
            onClick={() => setVoted(!voted)}
            className={cn(
              "hidden md:flex flex-col items-center justify-center gap-1 px-8 py-5 rounded-xl border-2 transition-all",
              voted
                ? "bg-primary border-primary text-primary-foreground hover:bg-primary-hover"
                : "bg-card border-border hover:border-primary hover:bg-primary-subtle"
            )}
          >
            <ArrowUp className={cn("h-7 w-7", voted && "fill-current")} strokeWidth={2.5} />
            <span className="text-2xl font-bold">{voteCount}</span>
          </button>
        </div>

        {/* Mobile Vote */}
        <button
          onClick={() => setVoted(!voted)}
          className={cn(
            "md:hidden flex items-center justify-center gap-3 w-full mt-6 px-6 py-4 rounded-xl border-2 transition-all",
            voted
              ? "bg-primary border-primary text-primary-foreground hover:bg-primary-hover"
              : "bg-card border-border hover:border-primary"
          )}
        >
          <ArrowUp className={cn("h-5 w-5", voted && "fill-current")} strokeWidth={2.5} />
          <span className="text-lg font-bold">{voteCount} голосов</span>
        </button>
      </div>

      {/* Stats & Actions Bar */}
      <div className="px-6 md:px-8 py-4 bg-muted/40 border-t border-border flex flex-wrap items-center justify-between gap-4">
        {/* Stats */}
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2 text-foreground-secondary">
            <Eye className="h-4 w-4" />
            <span className="font-medium">{MOCK_METRICS.viewCount.toLocaleString('ru-RU')}</span>
          </div>
          <div className="flex items-center gap-2 text-foreground-secondary">
            <MessageCircle className="h-4 w-4" />
            <span className="font-medium">{MOCK_METRICS.commentCount}</span>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-foreground-tertiary">
            <Calendar className="h-4 w-4" />
            <span>{formatDistanceToNow(MOCK_METRICS.launchDate, { addSuffix: true, locale: ru })}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {header.websiteUrl && (
            <Button size="sm" className="gap-2">
              Открыть сайт
              <ExternalLink className="h-4 w-4" />
            </Button>
          )}
          <Button variant="outline" size="icon" className="h-9 w-9">
            <Share2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Founder Strip */}
      <div className="px-6 md:px-8 py-4 border-t border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10">
            {user?.avatarUrl ? (
              <AvatarImage src={user.avatarUrl} alt={user.name || ''} />
            ) : (
              <AvatarImage src={MOCK_FOUNDER.avatarUrl} alt={MOCK_FOUNDER.name} />
            )}
            <AvatarFallback>{(user?.name || MOCK_FOUNDER.name).slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm text-foreground">{user?.name || MOCK_FOUNDER.name}</span>
            </div>
            <span className="text-xs text-foreground-tertiary">Создатель продукта</span>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-4 text-sm text-foreground-secondary">
          <div className="flex items-center gap-1.5">
            <Package className="h-4 w-4" />
            <span>{MOCK_FOUNDER.productsLaunched} продукта</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Users className="h-4 w-4" />
            <span>{MOCK_FOUNDER.followers} подписчиков</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CONTENT SECTION - Product description and media
// ============================================================================

function ContentSection() {
  const { formData } = usePageBuilderContext();
  const { blocks } = formData;

  const hasRenderableVideo = (block: Extract<typeof blocks[number], { type: 'video' }>) =>
    Boolean(block.content.embedId || block.content.embedSrc);

  const hasContent = blocks.some((block) => {
    if (block.type === 'text') return block.content.plainText.trim().length > 0;
    if (block.type === 'image') return block.content.image !== null;
    if (block.type === 'video') return hasRenderableVideo(block);
    if (block.type === 'gallery') return block.content.images.length > 0;
    return false;
  });

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <div>
        {hasContent ? (
          <div>
            {blocks.map((block) => {
              if (block.type === 'text' && !block.content.plainText.trim()) return null;
              if (block.type === 'image' && !block.content.image) return null;
              if (block.type === 'video' && !hasRenderableVideo(block)) return null;
              if (block.type === 'gallery' && block.content.images.length === 0) return null;

              return (
                <div key={block.id}>
                  <BlockPreview block={block} />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mx-auto mb-4">
              <Eye className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="text-foreground-secondary">
              Добавьте контент в редакторе — изображения, видео или текст
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Q&A SECTION (matches ProductQASection from product detail page)
// ============================================================================

function QASection() {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(['qa2']));
  const { user } = useAuth();

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const founderName = user?.name || MOCK_FOUNDER.name;
  const founderAvatar = user?.avatarUrl || MOCK_FOUNDER.avatarUrl;

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">Вопросы основателю</h2>
        <Button variant="outline" size="sm">
          <MessageCircle className="h-4 w-4 mr-2" />
          Задать вопрос
        </Button>
      </div>

      <div className="divide-y divide-border">
        {MOCK_QA_ITEMS.map((qa) => {
          const isExpanded = expandedIds.has(qa.id);
          return (
            <div key={qa.id}>
              <button
                onClick={() => toggleExpanded(qa.id)}
                className="w-full px-6 py-4 flex items-start gap-3 text-left hover:bg-muted/30 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-foreground">{qa.question}</h3>
                  <p className="text-xs text-foreground-tertiary mt-1">
                    Спросил <span className="text-foreground-secondary">{qa.askedBy}</span>
                    {' · '}
                    {formatDistanceToNow(qa.answeredAt, { addSuffix: true, locale: ru })}
                  </p>
                </div>
                <ChevronDown className={cn("h-5 w-5 text-foreground-tertiary transition-transform", isExpanded && "rotate-180")} />
              </button>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-4">
                      <div className="flex items-start gap-3 ml-4 p-4 bg-muted/50 rounded-xl">
                        <Avatar className="h-8 w-8 flex-shrink-0">
                          <AvatarImage src={founderAvatar} alt={founderName} />
                          <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">
                            {founderName.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm">{founderName}</span>
                            <span className="text-xs px-1.5 py-0.5 rounded-md bg-emerald-600/90 text-white font-medium">
                              Основатель
                            </span>
                            <span className="text-xs text-foreground-tertiary">
                              {formatDistanceToNow(qa.answeredAt, { addSuffix: true, locale: ru })}
                            </span>
                          </div>
                          <p className="text-sm text-foreground-secondary">{qa.answer}</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// COMMENT VOTE ICONS (matches product-comments.tsx)
// ============================================================================

function UpArrow({ active }: { active?: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M8 3L14 9H10V13H6V9H2L8 3Z"
        fill={active ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DownArrow({ active }: { active?: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M8 13L2 7H6V3H10V7H14L8 13Z"
        fill={active ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ============================================================================
// COMMENTS SECTION (matches ProductComments from product detail page)
// ============================================================================

function CommentsSection() {
  const [sortBy, setSortBy] = useState<'top' | 'new' | 'oldest'>('top');
  const { user } = useAuth();

  const founderName = user?.name || MOCK_FOUNDER.name;
  const founderAvatar = user?.avatarUrl || MOCK_FOUNDER.avatarUrl;

  // Replace the mock founder identity in comments with the current user's
  const comments = MOCK_COMMENTS.map((comment) => ({
    ...comment,
    replies: comment.replies?.map((reply) =>
      reply.userName === MOCK_FOUNDER.name
        ? { ...reply, userName: founderName, userAvatar: founderAvatar }
        : reply
    ),
  }));

  const total = comments.reduce((n, c) => n + 1 + (c.replies?.length || 0), 0);

  return (
    <div className="bg-card rounded-2xl overflow-hidden border border-border">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border/50 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium">
          <MessageSquare className="w-4 h-4 text-muted-foreground" />
          <span>{total} комментариев</span>
        </div>
        <div className="flex gap-0.5 text-xs">
          {(['top', 'new', 'oldest'] as const).map((o) => (
            <button
              key={o}
              onClick={() => setSortBy(o)}
              className={cn(
                "px-2.5 py-1 rounded-full",
                sortBy === o ? "bg-muted text-foreground font-medium" : "text-muted-foreground hover:text-foreground"
              )}
            >
              {o === 'top' ? 'Лучшие' : o === 'new' ? 'Новые' : 'Старые'}
            </button>
          ))}
        </div>
      </div>

      {/* Comment Input */}
      <div className="p-4 border-b border-border/50">
        <div className="flex gap-3">
          <Avatar className="w-8 h-8 shrink-0">
            {user?.avatarUrl && <AvatarImage src={user.avatarUrl} />}
            <AvatarFallback className="text-xs bg-muted">{user?.name?.[0]?.toUpperCase() || '?'}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <textarea
              placeholder="Написать комментарий..."
              rows={1}
              className="w-full px-3 py-2 text-sm bg-muted/50 border-0 rounded-xl resize-none placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-border"
              readOnly
            />
          </div>
        </div>
      </div>

      {/* Comments List */}
      <div className="p-4">
        <ul className="space-y-0">
          {comments.map((comment, i) => (
            <PreviewCommentItem
              key={comment.id}
              comment={comment}
              depth={0}
              isLast={i === comments.length - 1}
              isFounderReply={false}
              founderName={founderName}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

function PreviewCommentItem({ comment, depth, isLast, isFounderReply, founderName }: {
  comment: PreviewComment;
  depth: number;
  isLast: boolean;
  isFounderReply: boolean;
  founderName: string;
}) {
  const hasKids = (comment.replies?.length ?? 0) > 0;

  return (
    <li className="relative list-none">
      {/* Mask parent's line for last child */}
      {depth > 0 && isLast && (
        <div
          className="absolute bg-card pointer-events-none z-10"
          style={{ left: -20, top: 5, bottom: 0, width: 8 }}
        />
      )}

      {/* L-shaped curved connector for nested replies */}
      {depth > 0 && (
        <div
          className="absolute border-l-2 border-b-2 border-border rounded-bl-xl pointer-events-none z-20"
          style={{ left: -19, top: 5, width: 22, height: 18 }}
        />
      )}

      {/* Vertical thread line from avatar to children */}
      {hasKids && (
        <div
          className="absolute"
          style={{ left: 4, top: 40, bottom: 0, width: 20 }}
        >
          <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-0.5 bg-border rounded-full" />
        </div>
      )}

      {/* Comment content */}
      <div className="flex gap-3 py-2">
        <Avatar className="w-8 h-8 shrink-0 z-30">
          <AvatarImage src={comment.userAvatar} />
          <AvatarFallback className="text-xs bg-muted">{comment.userName[0]}</AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="text-sm font-medium">{comment.userName}</span>
            {isFounderReply && (
              <span className="text-xs px-1.5 py-0.5 rounded-md bg-emerald-600/90 text-white font-medium">
                Основатель
              </span>
            )}
            <span className="text-xs text-muted-foreground">·</span>
            <span className="text-xs text-muted-foreground">
              {formatDistanceToNow(comment.createdAt, { addSuffix: true, locale: ru })}
            </span>
          </div>

          {/* Body */}
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{comment.content}</p>

          {/* Actions */}
          <div className="flex items-center gap-1 mt-2 -ml-1">
            {/* Votes */}
            <div className="flex items-center rounded-full bg-muted/50">
              <button className={cn("p-1.5 rounded-full", comment.hasUserVoted ? "text-orange-500" : "text-muted-foreground")}>
                <UpArrow active={!!comment.hasUserVoted} />
              </button>
              <span className={cn("text-xs font-medium min-w-[20px] text-center", comment.hasUserVoted && "text-orange-500")}>
                {comment.voteCount}
              </span>
              <button className="p-1.5 rounded-full text-muted-foreground">
                <DownArrow />
              </button>
            </div>

            <button className="px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted rounded-full transition-colors">
              Ответить
            </button>

            <button className="p-1.5 text-muted-foreground hover:bg-muted rounded-full transition-colors">
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Children */}
      {hasKids && (
        <ul className="ml-8 space-y-0">
          {comment.replies.map((reply, i) => (
            <PreviewCommentItem
              key={reply.id}
              comment={reply}
              depth={depth + 1}
              isLast={i === comment.replies.length - 1}
              isFounderReply={reply.userName === founderName}
              founderName={founderName}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

// ============================================================================
// RELATED PRODUCTS - Horizontal scroll
// ============================================================================

function RelatedProducts() {
  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">Похожие продукты</h2>
        <Button variant="ghost" size="sm" className="gap-1 text-foreground-secondary">
          Смотреть все
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {MOCK_RELATED_PRODUCTS.map((product) => (
            <div
              key={product.id}
              className="group p-4 rounded-xl border border-border hover:border-primary/50 hover:bg-muted/30 transition-all cursor-pointer"
            >
              <div className="w-12 h-12 overflow-hidden rounded-lg mb-3">
                <Image src={product.logoUrl} alt="" width={48} height={48} className="w-full h-full object-cover" />
              </div>
              <h3 className="font-semibold text-sm text-foreground mb-1 group-hover:text-primary transition-colors">
                {product.name}
              </h3>
              <p className="text-xs text-foreground-tertiary mb-3 line-clamp-1">{product.tagline}</p>
              <div className="flex items-center gap-1 text-foreground-secondary">
                <ArrowUp className="h-3.5 w-3.5" />
                <span className="text-sm font-medium">{product.voteCount}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN PREVIEW PANEL
// ============================================================================

export function PreviewPanel() {
  return (
    <div className="min-h-screen bg-background">
      <main className="py-6">
        <div className="max-w-7xl mx-auto px-4 md:px-6 space-y-6">
          <div>
            <HeroSection />
          </div>

          <div>
            <FeaturedPreviewSection />
          </div>

          <div>
            <ContentSection />
          </div>

          <div>
            <QASection />
          </div>

          <div>
            <CommentsSection />
          </div>

          <div>
            <RelatedProducts />
          </div>
        </div>
      </main>
    </div>
  );
}
